import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { api } from '../../lib/api';

function pad(n) {
  return String(n).padStart(2, '0');
}

export default function CountdownStrip() {
  const [next, setNext] = useState(null);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .tournaments('ongoing')
        .then((d) => {
          if (!alive) return;
          const now = Date.now();
          const ts = d.tournaments || [];
          const live = ts.find((t) => t.status === 'live');
          if (live) {
            setNext({ ...live, live: true });
            return;
          }
          const upcoming = ts
            .map((t) => ({ ...t, ts: new Date(t.datetime).getTime() }))
            .filter((t) => Number.isFinite(t.ts) && t.ts > now)
            .sort((a, b) => a.ts - b.ts);
          setNext(upcoming[0] || null);
        })
        .catch(() => {});
    };
    load();
    const i = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, []);

  useEffect(() => {
    if (!next || next.live) return;
    const tick = () => setLeft(Math.max(0, next.ts - Date.now()));
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [next]);

  if (!next) return null;

  if (next.live) {
    return (
      <div className="bg-[#e8173a]/15 border-b border-[#e8173a]/30 py-3" data-testid="countdown-strip">
        <div className="max-w-7xl mx-auto px-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center">
          <span className="w-2 h-2 rounded-full bg-[#e8173a] animate-pulse" />
          <span className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-[#e8173a]" />
            Live now
          </span>
          <span className="text-[11px] text-[#c9c9c9] uppercase tracking-wider font-semibold">{next.title}</span>
        </div>
      </div>
    );
  }

  const secs = Math.floor(left / 1000);
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;

  return (
    <div className="bg-[#101320] border-b border-[#e8173a]/20 py-3" data-testid="countdown-strip">
      <div className="max-w-7xl mx-auto px-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center">
        <span className="w-2 h-2 rounded-full bg-[#e8173a] animate-pulse" />
        <span className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a]">Next battle drops in</span>
        <span className="font-black italic text-white text-base sm:text-lg tabular-nums" data-testid="countdown-timer">
          {d > 0 && <span>{pad(d)}<span className="text-[#e8173a]">D</span> : </span>}
          {pad(h)}
          <span className="text-[#e8173a]">H</span> : {pad(m)}
          <span className="text-[#e8173a]">M</span> : {pad(s)}
          <span className="text-[#e8173a]">S</span>
        </span>
        <Link
          to="/tournaments"
          data-testid="countdown-tournament-link"
          className="text-[11px] text-[#9e9e9e] hover:text-[#e8173a] uppercase tracking-wider font-semibold transition-colors truncate max-w-[240px] sm:max-w-none"
        >
          {next.title}
        </Link>
      </div>
    </div>
  );
}
